import { schemaImportAllees } from '@cc/contrats';
import { Bandeau, Bouton, Champ } from '@cc/ui';
import { useState } from 'react';
import type { z } from 'zod';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { INVALIDER_MARCHES } from './api';
import styles from './marches.module.css';

export function ImportAllees({ marcheId }: { marcheId: string }) {
  const [importation, setImportation] = useState<z.infer<typeof schemaImportAllees> | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const action = useAction({
    executer: () =>
      requete(`/marches/${marcheId}/allees/importer`, { methode: 'POST', corps: importation }),
    succes: 'Allées importées. Leur position reste schématique.',
    invalider: INVALIDER_MARCHES,
    apres: () => setImportation(null),
  });
  const lire = async (fichier: File | undefined) => {
    setImportation(null);
    setErreur(null);
    if (!fichier) return;
    try {
      if (fichier.size > 2_000_000) throw new Error('Fichier limité à 2 Mo.');
      const brut: unknown = JSON.parse(await fichier.text());
      const allees =
        typeof brut === 'object' && brut !== null && 'allees' in brut ? brut.allees : undefined;
      const resultat = schemaImportAllees.safeParse({ allees });
      if (!resultat.success)
        throw new Error(
          'Fichier d’allées invalide : vérifiez les noms, coordonnées et références de source.',
        );
      setImportation(resultat.data);
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };
  return (
    <div className={styles.pile}>
      <Champ
        type="file"
        accept="application/json,.json"
        libelle="Importer des allées géographiques"
        onChange={(e) => void lire(e.target.files?.[0])}
      />
      <p className={styles.aide}>
        Les allées OpenStreetMap restent à vérifier sur le terrain. Données © les contributeurs
        d’OpenStreetMap, licence ODbL.
      </p>
      {erreur || action.messageErreur ? (
        <Bandeau type="erreur">{erreur ?? action.messageErreur}</Bandeau>
      ) : null}
      {importation ? (
        <>
          <p>
            {importation.allees.length} allée(s) à importer :{' '}
            {importation.allees.map((a) => a.nom).join(', ')}.
          </p>
          <Bouton disabled={action.enCours} onClick={() => action.lancer(undefined)}>
            Confirmer l’import des allées
          </Bouton>
        </>
      ) : null}
    </div>
  );
}
